import Log from 'providers/log';
import { Dispatch } from 'react-redux';
import { ThunkAction } from 'redux-thunk';
// import React from 'react';

import Shopify from 'providers/shopify';
import slugify from 'slugify';
import CartItem from 'types/shopify/CartItem';
import Product from 'types/shopify/Product';
import { IRootState } from '../root_reducer';

const ADD = 'gruhn-guitars-modern/shopify/ADD';
const REMOVE = 'gruhn-guitars-modern/shopify/REMOVE';
const UPDATE = 'gruhn-guitars-modern/shopify/UPDATE';
const CLEAR = 'gruhn-guitars-modern/shopify/CLEAR';

const SET_CART_MESSAGE = 'gruhn-guitars-modern/shopify/SET_CART_MESSAGE';

/** Dispatch this action in the constructor or will/didmount methods of shopify related components */
// const LOAD = 'gruhn-guitars-modern/shopify/LOAD';
const SET_PRODUCTS = 'gruhn-guitars-modern/shopify/SET_PRODUCTS';
/** !!Only used when loading from local storage. If that changes, wrap updated state in cartUpdated call so local storage will be updated!! */
const SET_CART = 'gruhn-guitars-modern/shopify/SET_CART';
const SET_URL = 'gruhn-guitars-modern/shopify/SET_URL';
/** Since render is called when shopify cart and products are updated, we use a seperate flag to prevent multiple loads from being initiated */
const SET_IS_LOADING = 'gruhn-guitars-modern/shopify/SET_IS_LOADING';
/** Since render is called when shopify cart and products are updated, we use a seperate flag to prevent multiple loads from being initiated */
const SET_NOT_LOADING = 'gruhn-guitars-modern/shopify/SET_NOT_LOADING';
const FINISHED_LOADING = 'gruhn-guitars-modern/shopify/FINISHED_LOADING';

const MENU_PRODUCTS_GET = 'gruhn-guitars-modern/shopify/MENU_PRODUCTS_GET';
const MENU_PRODUCTS_SUCCESS = 'gruhn-guitars-modern/shopify/MENU_PRODUCTS_SUCCESS';
const MENU_PRODUCTS_FAIL = 'gruhn-guitars-modern/shopify/MENU_PRODUCTS_FAIL';
const ACCESSORIES_MENU_DICTIONARY_SET = 'accessories-menu-dictionary-set';

interface TypeOnlyAction {
    type: string;
}

interface CartItemAction {  // For ADD, REMOVE, UPDATE, CLEAR
    type: string;
    item: CartItem;
}

interface CartBulkAction { // For SET_CART
    type: string;
    cartItems: CartItem[];
}

interface ProductAction { // For SET_PRODUCTS
    type: string;
    productItems: Product[];
}

interface StringArgAction {
    type: string;
    value: string;
}

/** IMPORTANT: the type member is used to determine which interface is being represented by Action. Keep this in mind when updating the reducer and its type checking */
export type Action = TypeOnlyAction | CartItemAction | CartBulkAction | ProductAction | StringArgAction;

export interface State {
    cartItems: CartItem[];
    hasLoaded: boolean; /** Data has finished loading without error */
    isLoading: boolean; /** Data is currently being loaded */
    products: Product[];
    url: string;
    menuProducts: Product[];
    menuProductsLoading: boolean;
    message: string;
    shopifyCollectionsLoading: boolean;
    shopifyCollections: any; // TODO - fix any
    accessoriesMenuDictionary: any;
}

const cartUpdated = (newState: State): State => {
    Shopify.updateCartInLocalStorage(newState.cartItems);
    return newState;
};

const initialState = (): State => {
    return {
        accessoriesMenuDictionary: null,
        cartItems: [],
        hasLoaded: false,
        isLoading: false,
        menuProducts: [],
        menuProductsLoading: false,
        message: '',
        products: [],
        shopifyCollections: null,
        shopifyCollectionsLoading: false,
        url: '',
    };
};

// reducer function
export default function (state: State = initialState(), action: Action): State {
    if (action.type.indexOf('gruhn-guitars-modern/shopify/') !== -1) {    // Dont check types on unrelated actions
        if (action.type === SET_URL) {
            if (!(action as StringArgAction).value) {
                Log.error(`url is required for the SET_URL action`, 'shopify.reducer()');
                return state;
            }
        } else if (action.type === SET_PRODUCTS) {
            if (!(action as ProductAction).productItems) {
                Log.error(`productItems are required for the SET_PRODUCTS action`, 'shopify.reducer()');
                return state;
            }
        } else if (action.type === SET_CART) {
            if (!(action as CartBulkAction).cartItems) {
                Log.error(`cartItems are required for the SET_CART action`, 'shopify.reducer()');
                return state;
            }
        } else if (action.type === ADD
            || action.type === REMOVE
            || action.type === UPDATE) {
            if (!(action as CartItemAction).item) {
                Log.error(`Item is required for actions ADD, REMOVE, and UPDATE. Current action: ${action.type}`, 'shopify.reducer()');
                return state;
            }
        }
    }
    switch (action.type) {
        case ADD:
            return cartUpdated({ ...state, cartItems:[...state.cartItems, (action as CartItemAction).item] });
        case REMOVE:
            return cartUpdated({ ...state, cartItems:[...state.cartItems.filter(item => item.addedTime !== (action as CartItemAction).item.addedTime)] });
        case UPDATE:
            return cartUpdated({ ...state, cartItems: state.cartItems.map((item) => { // map to maintain ordering
                return item.addedTime !== (action as CartItemAction).item.addedTime
                    ? item
                    : (action as CartItemAction).item;
            }) });
        case CLEAR:
            Shopify.clearLocalStorage();
            return { ...state, cartItems: [] };
        case SET_PRODUCTS:
            return { ...state, products: (action as ProductAction).productItems };
        case SET_CART:  // !!Only used when loading from local storage. If that changes, wrap updated state in cartUpdated as seen above so local storage will be updated!!
            return { ...state, cartItems: (action as CartBulkAction).cartItems };
        case SET_URL:
            return { ...state, url: (action as StringArgAction).value };
        case SET_CART_MESSAGE:
            return { ...state, message: (action as StringArgAction).value };
        case SET_IS_LOADING:
            return { ...state, isLoading: true };
        case SET_NOT_LOADING:
            return { ...state, isLoading: false };
        case FINISHED_LOADING:
            return { ...state, hasLoaded: true };
        case MENU_PRODUCTS_GET:
            return { ...state, menuProductsLoading: true };
        case MENU_PRODUCTS_SUCCESS:
            return { ...state, menuProductsLoading: false, menuProducts: (action as ProductAction).productItems };
        case MENU_PRODUCTS_FAIL:
            return { ...state, menuProductsLoading: false };
        case 'SHOPIFY_COLLECTIONS_GET':
            return { ...state, shopifyCollectionsLoading: true };
        case 'SHOPIFY_COLLECTIONS_SUCCESS':
            return { ...state, shopifyCollectionsLoading: false, shopifyCollections: (action as any).collections }; // TODO - Fix any
        case ACCESSORIES_MENU_DICTIONARY_SET:
            return { ...state, accessoriesMenuDictionary: (action as any).accessoriesMenuDictionary };
        default:
            return state;
    }
}

/**
 * getAccessoriesMenu
 * Thread of related functions
 * 1. getProductCollection - apps/client/src/providers/shopify.ts
 * 2. gruhn_react_shopify_get_collections - apps/drupal/sites/all/modules/custom/gruhn_react_shopify/gruhn_react_shopify.module
 * 3. getProductsInCollection - apps/drupal/react-shopify/gruhn_react_shopify/shopify/shopifyapi.php
 * Note-one: #3 may need to be swapped out for a method that one of the following alternatives
 * Alt 1) https://help.shopify.com/en/api/reference/products/smartcollection
 * Alt 2) https://help.shopify.com/en/api/reference/products/customcollection
 * Note-two: There is no "limit" on product retrieval
 */
export const getAccessoriesMenu = (collectionId: number): any => (dispatch: Dispatch<any>) => {
    dispatch({ type: MENU_PRODUCTS_GET });
    Shopify.getProductCollection(collectionId)
        .then((data) => {
            // Get the dictionay into the Redux state here, below
            dispatch({ type: MENU_PRODUCTS_SUCCESS, productItems: data });
        });
};

// TODO - abstract strings to constants
export const getShopifyCollections = (): any => (dispatch: Dispatch<any>) => {
    // dispatch({ type: MENU_PRODUCTS_GET });
    dispatch({ type: 'SHOPIFY_COLLECTIONS_GET' });
    Shopify.getShopifyCollections()
        .then((data: any) => { // Fix any
            dispatch({ type: 'SHOPIFY_COLLECTIONS_SUCCESS', collections: data });
        });
};
/**
 * Load all the things
 */
export const load = (): ThunkAction<void, IRootState, null, any> => (dispatch: Dispatch<any>, getState) => {

    /**
     * from Linkous
     * Only want to load once regardless of re-rendering.
     * For updated products, refresh the page
     */
    if (!getState().shopify.isLoading && !getState().shopify.hasLoaded) {

        dispatch({
            type: SET_IS_LOADING
        });

        /**
         * from Linkous
         * Nothing depends on this except
         * the checkout procedure
         * which shouldnt happen until long after init
         */
        Shopify.getUrl().then(value => dispatch({
            type: SET_URL, value
        }));

        Shopify.getProducts().then((products) => {
            const accessoriesMenuDictionary: any = {};
            for (const product of products) { // TODO - conver to other for loop
                const handle = product.handle;
                const productType = product.product_type;
                const typeSlug = slugify(productType);

                if (!(accessoriesMenuDictionary[typeSlug])) {
                    accessoriesMenuDictionary[typeSlug] = { type: productType, handles: [handle] };
                } else {
                    accessoriesMenuDictionary[typeSlug].handles.push(handle);
                }
            }
            dispatch({ type: ACCESSORIES_MENU_DICTIONARY_SET, accessoriesMenuDictionary });
            dispatch({
                productItems: products,
                type: SET_PRODUCTS
            });
            return products;
        }).then((products) => {
            return {
                products,
                storedCart: Shopify.loadCartFromLocalStorage()
            };
        }).then(({ products, storedCart }) => {
            if (storedCart.length > 0) {
                dispatch({
                    cartItems: storedCart, // removed -nov-20-2018- Shopify.pruneInvalidProducts(storedCart, products)
                    type: SET_CART
                });
            }
            if (products && products.length > 0) {
                dispatch({
                    type: FINISHED_LOADING
                });
            }
        }).catch((error) => {
            Log.error(error, 'shopify.load()');
        }).then(() => {
            dispatch({
                type: SET_NOT_LOADING
            });
        });
    } else {
        Log.info(`Data load already ${getState().shopify.hasLoaded ? 'completed' : 'started'}`, 'shopify.load()');
    }
};

export const add = (item: CartItem): CartItemAction => ({ type:ADD, item });
export const remove = (item: CartItem): CartItemAction => ({ type:REMOVE, item });
export const clear = (): TypeOnlyAction => ({ type:CLEAR });
export const update = (item: CartItem): CartItemAction => ({ type:UPDATE, item });
export const cartMessage = (value: string): StringArgAction => ({ type: SET_CART_MESSAGE, value });



// WEBPACK FOOTER //
// ./src/redux/modules/shopify.ts